import { Button, Dropdown, Flex, Input } from "antd";
import { TextAreaRef } from "antd/es/input/TextArea";
import FlexContainerLayout from "components/atoms/FlexContainerLayout";
import { useCallback, useRef, useState } from "react";

const { TextArea } = Input;

interface ITextFilePreviewProps {
    content: string | null;
    enableColumnSpec: boolean;
    selectedColumnSpecIndex: number;
    onAddColumnSpec: (spec: ISchemaFileColumnSpec) => void;
    onInsertColumnSpec: (spec: ISchemaFileColumnSpec) => void;
}

function TextFilePreview({ content, enableColumnSpec = false, onInsertColumnSpec, onAddColumnSpec, selectedColumnSpecIndex }: ITextFilePreviewProps) {
    const textAreaRef = useRef<TextAreaRef>(null);
    const [columnSpecButtonStatus, setColumnSpecButtonStatus] = useState({ addDisabled: true, insertDisabled: true });

    const onContextMenuClick = useCallback(
        ({ key }: { key: string }) => {
            if (textAreaRef.current && textAreaRef.current.resizableTextArea && textAreaRef.current.resizableTextArea.textArea) {
                if (key === "INSERT_COLUMN_SPEC") {
                    onInsertColumnSpec &&
                        onInsertColumnSpec({
                            start: textAreaRef.current.resizableTextArea.textArea.selectionStart + 1,
                            end: textAreaRef.current.resizableTextArea.textArea.selectionEnd + 1,
                            name: content
                                ? content.slice(textAreaRef.current.resizableTextArea.textArea.selectionStart, textAreaRef.current.resizableTextArea.textArea.selectionEnd)
                                : "",
                        });
                } else if (key === "ADD_COLUMN_SPEC") {
                    onAddColumnSpec &&
                        onAddColumnSpec({
                            start: textAreaRef.current.resizableTextArea.textArea.selectionStart + 1,
                            end: textAreaRef.current.resizableTextArea.textArea.selectionEnd + 1,
                            name: content
                                ? content.slice(textAreaRef.current.resizableTextArea.textArea.selectionStart, textAreaRef.current.resizableTextArea.textArea.selectionEnd)
                                : "",
                        });
                }
            }
        },
        [content, onAddColumnSpec, onInsertColumnSpec],
    );

    const onTextAreaMouseUp = useCallback(() => {
        if (textAreaRef.current && textAreaRef.current.resizableTextArea && textAreaRef.current.resizableTextArea.textArea) {
            setColumnSpecButtonStatus({ addDisabled: false, insertDisabled: selectedColumnSpecIndex === -1 });
        }
    }, [selectedColumnSpecIndex]);

    const onActionButtonClick = useCallback(
        (key: string) => {
            onContextMenuClick({ key });
            setColumnSpecButtonStatus({ addDisabled: true, insertDisabled: true });
            if (textAreaRef.current && textAreaRef.current.resizableTextArea && textAreaRef.current.resizableTextArea.textArea) {
                const selectedWindow = window.getSelection();
                const selectedDocument = document.getSelection();
                if (selectedWindow) {
                    selectedWindow.removeAllRanges();
                } else if (selectedDocument) {
                    selectedDocument.empty();
                }
                textAreaRef.current.resizableTextArea.textArea.selectionStart = textAreaRef.current.resizableTextArea.textArea.selectionEnd;
            }
        },
        [onContextMenuClick],
    );

    return (
        <FlexContainerLayout
            showHeader={enableColumnSpec}
            showFooter={false}
            header={
                enableColumnSpec && (
                    <Flex gap={6}>
                        <Button type="primary" size="small" ghost disabled={columnSpecButtonStatus.addDisabled} onClick={() => onActionButtonClick("ADD_COLUMN_SPEC")}>
                            Add column spec
                        </Button>
                        <Button type="primary" size="small" ghost disabled={columnSpecButtonStatus.insertDisabled} onClick={() => onActionButtonClick("INSERT_COLUMN_SPEC")}>
                            Insert column spec
                        </Button>
                    </Flex>
                )
            }
            contentClassName="text-file-preview"
        >
            <Dropdown
                menu={
                    enableColumnSpec
                        ? {
                              onClick: onContextMenuClick,
                              items: [
                                  {
                                      label: "Insert column spec",
                                      key: "INSERT_COLUMN_SPEC",
                                      disabled: selectedColumnSpecIndex === -1,
                                  },
                                  {
                                      label: "Add column spec",
                                      key: "ADD_COLUMN_SPEC",
                                  },
                              ],
                          }
                        : undefined
                }
                trigger={enableColumnSpec ? ["contextMenu"] : []}
            >
                <TextArea
                    name="file-content"
                    style={{ fontFamily: "'Courier Prime', monospace" }}
                    className="box-border flex-1"
                    ref={textAreaRef}
                    value={content ?? ""}
                    onMouseUp={onTextAreaMouseUp}
                />
            </Dropdown>
        </FlexContainerLayout>
    );
}

export default TextFilePreview;
